import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HookFormContext } from 'components/cef_form/hook-form/providers/HookFormProvider';
import CheckBox from 'components/ui-components/CheckBox';
import Button from 'components/ui-components/Button';
import LoadingSpinner from 'components/ui-components/loaders/LoadingSpinner';
import { WarningTriangle } from 'assets/icons';
import AlertBox from 'components/ui-components/AlertBox';
import { useHTTP } from 'hooks/useRequest';
import GoogleRecaptcha from 'components/reCAPTCHA/GoogleRecaptcha';
import MetaHelmet from 'helpers/MetaHelmet';
import StepTemplate from '../StepTemplate';
import DetailContainer from './DetailContainer';
import ContactOptions from './ContactOptions';

const Summary = () => {
  const {
    setCurrentStep,
    companyTypes,
    businessTypeData,
    recaptchaKeys,
    watch,
    watchedFields: {
      companyType,
      customer: {
        business_type: businessType,
        title,
        forename,
        surname,
        email,
        telephone,
      },
      accountCashOnly,
      companyName,
      companyNumber,
      companyVatNumber,
      addressOne,
      ownProperty,
      propertyDuration,
      addressTwo,
      billingAddressMatchesRegistered,
      accountContactName,
      accountContactEmail,
      accountContactPhone,
      purchasingContactName,
      purchasingContactEmail,
      purchasingContactPhone,
      dateOfBirth,
    },
  } = useContext(HookFormContext);

  const navigate = useNavigate();

  const [contactOptions, setContactOptions] = useState({
    marketing_opt_in: '1',
    direct_mail_communication: '0',
    email_communication: '0',
    sms_communication: '0',
    phone_communication: '0',
  });
  const [termsAndConditions, setTermsAndConditions] = useState('0');
  const [termsError, setTermsError] = useState(false);
  const [clickedCreateAccount, setClickedCreateAccount] = useState(false);
  const [response, setResponse] = useState({ status: undefined, data: {} });
  const [error, setError] = useState('');
  const [genericError, setGenericError] = useState(false);
  const [updatedForm, setUpdatedForm] = useState({});
  const [token, setToken] = useState();

  const [v2Key, v3Key] = recaptchaKeys;

  const { day, month, year } = dateOfBirth;

  const completeForm = watch();

  const requestConfig = {
    url: token ? '/mycef/account' : null,
    method: 'post',
    data: { ...updatedForm, 'g-recaptcha-response': token },
  };

  const { error: err, data: res, loading } = useHTTP(requestConfig);

  useEffect(() => {
    setToken('');
    if (res) {
      // a successful res will return an object
      if (typeof res === 'object' && !Array.isArray(res)) {
        setResponse({ status: 200, data: res });
        // a failed res will return an array
      } else if (res.length >= 1) {
        // successful call and issue with data return error
        setResponse({
          status: res[1].status,
          data: res[0].error,
        });
      }
    }
  }, [res]);

  useEffect(() => {
    setToken('');
    if (err === undefined) return;
    // recaptcha fails, call never made, show v2
    if (err.error.status === 400) {
      // This enables manual entry
      setResponse({
        status: 400,
        data: err.error.data.error || 'recaptcha failed',
        responseType: err.error.data.response_type,
      });
    } else if (err.error.status === 404 || err.error.status === 503) {
      // call fails but recaptcha successful return error
      setResponse({
        status: err.error.status,
        data: err.error.statusText,
      });
    }
  }, [err]);

  useEffect(() => {
    setCurrentStep(5);
  }, []);

  // Handle error and success logic
  useEffect(() => {
    setClickedCreateAccount(false);
    if (response.status === 200) {
      if (response.data.state === 'questions') {
        navigate('../identity-verification', {
          state: response.data.questions,
        });
      } else {
        navigate('../registration-success', {
          state: updatedForm,
        });
      }
    } else if (
      response.status === 403 || // reCAPTCHA failed validation
      response.status === 406 // Not acceptable response
    ) {
      setClickedCreateAccount(false);
    } else if (
      response.status === 400 &&
      response.data !== 'recaptcha failed'
    ) {
      // Scroll to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });

      if (typeof response.data === 'object') {
        setError(
          `
            <p>
              Sorry, we were unable to create your account. Please check you
              have provided the following and try again:
            </p>
            <ul>
              ${Object.keys(response.data)
                .map(key => `<li>${key}: ${response.data[key][0]}</li>`)
                .join('')}
            </ul>
          `,
        );
      } else if (response.data === 'unsuccessful') {
        setGenericError(true);
      } else if (
        response.responseType === 'service_error' &&
        response.error === 'unsuccessful'
      ) {
        // Redirect to 'Unsuccessful' view
        navigate('../registration-unsuccessful');
        setClickedCreateAccount(false);
      } else {
        setError(response.data);
      }
    }

    setClickedCreateAccount(false);
  }, [response]);

  useEffect(() => {
    // Create correct data format for BE

    // The below conditionals are necessary as the selects
    // can default to the text content of the placeholder

    if (completeForm.customer.title === 'Select') {
      completeForm.customer.title = '';
    }

    if (completeForm.customer.business_type === 'Please select an option') {
      completeForm.customer.business_type = null;
    }

    const updatedCustomerData = {
      ...completeForm.customer,
      ...contactOptions,
      terms_and_conditions: termsAndConditions,
    };

    completeForm.customer = updatedCustomerData;

    setUpdatedForm(completeForm);
  }, [contactOptions, termsAndConditions]);

  useEffect(() => {
    // Need to reset clickedCreateAccount so recaptchas submit useEffect can run
    if (clickedCreateAccount) {
      setTimeout(() => {
        setClickedCreateAccount(false);
      }, 0);
    }
  }, [clickedCreateAccount]);

  const moveToStep = step => {
    navigate(`../${step}`);
    window.scrollTo(0, 0);
  };

  // TODO: Possibly refactor to do something like:
  // const findBusinessType = typeId => (businessTypeData.find(item => item.value === typeId)?.label || '')
  const findBusinessType = typeId => {
    const type = businessTypeData.find(({ value }) => value === typeId);
    return type?.label || '';
  };

  const handleFormSubmit = () => {
    setError('');
    setGenericError(false);
    if (termsAndConditions === '1') {
      setTermsError(false);
      setClickedCreateAccount(true);
      // Submit data and marketing preferences
    } else if (termsAndConditions === '0') {
      setTermsError(true);
    }
  };

  const { label } = companyTypes.find(company => company.value === companyType);

  // Set each sections details to there own array
  const companyDetails = [
    { name: 'Company Type', value: label },
    {
      name: 'Business Type',
      value: findBusinessType(Number(businessType)),
    },
    {
      name: 'Do you want to apply for credit?',
      value: accountCashOnly ? 'No' : 'Yes',
    },
    { name: 'Full Company Name', value: companyName },
    {
      name: 'Company Number',
      value: companyNumber,
      // the only case for this being optional is for sole trader.
      // Hence the check for id of 1
      optional: companyType === '1',
    },
    { name: 'Company VAT Number', value: companyVatNumber, optional: true },
  ];

  const Addresses = [
    { name: 'Registered Office Address', value: addressOne },
    {
      name: 'Billing Address',
      value: billingAddressMatchesRegistered
        ? 'Same as Registered Office Address'
        : addressTwo,
    },
  ];

  const contactDetails = [
    { name: 'Title', value: title },
    { name: 'First Name', value: forename },
    { name: 'Last Name', value: surname },
    { name: 'Email', value: email },
    { name: 'Phone', value: telephone },
    {
      name: 'Accounts Contact',
      value: accountContactName
        ? {
            name: accountContactName,
            email: accountContactEmail,
            phone: accountContactPhone,
          }
        : false,
      optional: true,
    },
    {
      name: 'Purchasing Contact',
      value: purchasingContactName
        ? {
            name: purchasingContactName,
            email: purchasingContactEmail,
            phone: purchasingContactPhone,
          }
        : false,
    },
    {
      name: 'Date of Birth',
      value: day ? `${day}/${month}/${year}` : false,
    },
  ];

  const soleTraderAddresses = [
    {
      name: 'Home Address',
      value: addressOne,
    },
    {
      name: 'Do you own this property?',
      value: ownProperty ? 'Yes' : 'No',
    },
    {
      name: 'Have you been at this property more than 3 years?',
      value: propertyDuration ? 'Yes' : 'No',
    },
    {
      name: 'Previous Address',
      value: propertyDuration ? false : addressTwo,
    },
  ];

  // Create an object for each section with title and specific details
  const detailSections = [
    {
      className: 'mb-55',
      sectionTitle: 'Company Details',
      details: companyDetails,
      onClick: () => moveToStep('step2'),
    },
    {
      className: 'mb-55',
      sectionTitle: 'Addresses',
      details: companyType === '1' ? soleTraderAddresses : Addresses,
      onClick: () => moveToStep('step3'),
    },
    {
      className: 'mb-38',
      sectionTitle: 'Contact Details',
      details: contactDetails,
      onClick: () => moveToStep('step4'),
    },
  ];

  return (
    <>
      <MetaHelmet />
      <StepTemplate step={4}>
        {error && (
          <AlertBox
            className="plr-13 pt-10 pb-13 mb-12 summary-submission-error"
            variant="error"
            iconHeight="20"
            body={error}
            bodyClass="fs-14 tc-cef-red ff-body"
          />
        )}
        {/* Keeping the below pre tags for testing purposes */}
        {/* <pre>{JSON.stringify([addressOne, addressTwo], null, 2)}</pre> */}
        {detailSections.map(
          ({ className, sectionTitle, details, step, onClick }) => (
            <DetailContainer
              key={sectionTitle}
              contClass={className}
              title={sectionTitle}
              titleClass="summary-title"
              details={details}
              detailClass="fw-400"
              step={step}
              btnText="Edit"
              onClick={() => onClick()}
            />
          ),
        )}
        <ContactOptions
          contactOptions={contactOptions}
          setContactOptions={e => setContactOptions(e)}
        />
        <CheckBox
          key="termsAndConditions"
          id="termsAndConditions"
          value={termsAndConditions !== '0'}
          onChange={() =>
            setTermsAndConditions(curState => (curState === '0' ? '1' : '0'))
          }
          label='I have read and accept the <a href="/info/terms_and_conditions" target="_blank" rel="noreferrer">Terms and Conditions</a>'
          className="fl mt-14 mb-3"
          labelClass="fw-400"
          error={termsError}
          errorMsg="Please confirm you have read the Terms and Conditions"
        />
        <GoogleRecaptcha
          res={res}
          error={err}
          submit={clickedCreateAccount}
          action="create"
          siteKeyV3={v3Key}
          siteKeyV2={v2Key}
          setToken={setToken}
          className="mt-8"
          v2Error="Please complete the reCaptcha above"
          v2ErrorClass="cef-ui-input-error cef-ui-input__error mb-0"
        />
        {/* Error to show for creditSafe */}
        {genericError && (
          <div className="fl plr-14 pt-10 pb-13 mb-12 mt-24 cef-ui-alertbox cef-ui-alertbox--error summary-submission-error">
            <WarningTriangle height="20" className="mr-8" />
            <div className="cef-ui-card__error-content">
              <p className="fs-14 tc-cef-red ff-body">
                Sorry, we were unable to create your account. Please check that
                the details you have provided are correct and try again.
              </p>
              <p className="fs-14 tc-cef-red ff-body mb-0">
                If the problem persists, please visit your local branch to apply
                in person and complete an ID check. You can either fill out an
                application form at the branch or download the{' '}
                <a
                  href="https://assets.cef.co.uk/misc/cef/CEF_Account_Application.pdf"
                  target="_blank"
                  rel="noreferrer">
                  Trade Account Application Form
                </a>{' '}
                and bring the completed form with you.
              </p>
            </div>
          </div>
        )}
        <Button
          className="cef-btn cef-btn--feature cef-btn--green width-100 m-width-auto cef-btn--xxl mt-24"
          type="button"
          disabled={loading}
          handleClick={() => handleFormSubmit()}>
          {loading ? <LoadingSpinner /> : 'Create Account'}
        </Button>
      </StepTemplate>
    </>
  );
};

export default Summary;
